import React, {
  FunctionComponent,
  useLayoutEffect,
  useRef,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { StyledAllEvents } from './styledComponents';
import AllEventsItem from './components/AllEventsItem/AllEventsItem.component';
import { useAllEvents } from '../../api/useAllEvents';
import SplashScreenComponent from '../../components/SplashScreen/SplashScreen.component';
import useDebounce from '../../hooks/useDebounce';
import EmptyPageComponent from '../../components/EmptyPage/EmptyPage.component';
import {
  AppQueryParamsConfigMap,
} from '../../app/constants';
import { useQueryParams } from 'use-query-params';
import { Helmet } from 'react-helmet';
import { APP_NAME_POSTFIX } from '../../constants';
import { setScrollPosition } from './AllEvents.slice';
import ReactPaginate from 'react-paginate';
import { useScrollOnSearch } from '../../hooks/useScrollOnSearch';

const connector = connect(
  (state: RootState) => ({
    searchQuery: state.app.searchQuery.allEvents,
    scrollPosition: state.allEvents.scrollPosition,
  }),
  { setScrollPosition },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const AllEvents: FunctionComponent<PropsFromRedux> = ({
  searchQuery,
  setScrollPosition,
  scrollPosition,
}) => {
  const debouncedSearchQuery = useDebounce(searchQuery, 600);
  // const queryClient = useQueryClient();

  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = scrollPosition;
    }, 0);

    return () => {
      // eslint-disable-next-line
      setScrollPosition(
        document.documentElement.scrollTop || document.body.scrollTop || 0,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: eventsData,
    status,
    changePage,
    isFetching,
    pageSize,
    page,
    isPreviousData,
  } = useAllEvents(debouncedSearchQuery);

  const [query] = useQueryParams(AppQueryParamsConfigMap);

  useScrollOnSearch(containerRef, 600, -120);

  if (!eventsData?.events.length && searchQuery && status !== 'loading') {
    return (
      <div className="mt-20">
        <EmptyPageComponent title="Nothing found">
          Try entering just part of event name
        </EmptyPageComponent>
      </div>
    );
  }

  const pageCount = eventsData
    ? Math.ceil(eventsData.totalCount / pageSize)
    : 0;


  return (
    <StyledAllEvents
      id="allEventsContainer"
      className="overflow-y-auto w-100"
      ref={containerRef}
      style={{
        opacity: isFetching && isPreviousData ? 0.4 : 1,
        pointerEvents: isFetching && isPreviousData ? 'none' : 'all',
      }}
    >
      <Helmet>
        <title>Calendar - {APP_NAME_POSTFIX}</title>
      </Helmet>
      {isFetching && !eventsData?.events.length && <SplashScreenComponent />}

      {eventsData?.events.map((item, index) => (
        <AllEventsItem
          showItemGroup={
            !index ||
            item.eventDateGroup !== eventsData.events[index - 1].eventDateGroup
          }
          item={item}
          key={item.eventId}
          active={Number(query.e) === item.eventId}
        />
      ))}
      {eventsData && pageCount > 1 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next"
          previousLabel="Previous"
          onPageChange={(e) => changePage(e.selected)}
          pageCount={pageCount}
          containerClassName="pagination justify-content-center"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          breakClassName="page-item"
          breakLinkClassName="page-link"
          forcePage={page}
        />
      )}
    </StyledAllEvents>
  );
};

export default connector(AllEvents);
